import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type ProductionPlaceFormValues,
  productionPlaceSchema,
} from '@/features/production-declaration/schema/production-place-schema';
import { useCreateProductionPlace } from '@/services/apis/mutations/production-declaration/use-create-production-place';
import { useUpdateProductionPlace } from '@/services/apis/mutations/production-declaration/use-update-production-place';
import type { ProductionPlace } from '@/types/core/production-declaration/production-place';

type UseProductionPlaceFormParams = {
  commodityOriginId: string;
  materialId: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  productionDeclarationId: string;
  productionPlace?: ProductionPlace;
  token?: string;
};

type ValidationErrorResponse = {
  errors?: Record<string, string[]>;
};

const defaultValues: ProductionPlaceFormValues = {
  coordinates: {
    type: 'Polygon',
    coordinates: [],
  },
  countryId: '',
  description: '',
  forestTypeId: '',
  productionTimeRange: '',
};

function getDefaultValues(
  productionPlace?: ProductionPlace,
): ProductionPlaceFormValues {
  if (!productionPlace) {
    return defaultValues;
  }

  return {
    coordinates: productionPlace.coordinates ?? defaultValues.coordinates,
    countryId: productionPlace.countryId.toString(),
    description: productionPlace.description ?? '',
    forestTypeId: productionPlace.forestType?.id.toString() ?? '',
    productionTimeRange: productionPlace.productionTimeRange ?? '',
  };
}

function setServerValidationErrors(
  error: unknown,
  setError: ReturnType<typeof useForm<ProductionPlaceFormValues>>['setError'],
) {
  if (!axios.isAxiosError<ValidationErrorResponse>(error)) {
    return;
  }

  const errors = error.response?.data?.errors;
  if (!errors) {
    return;
  }

  for (const [field, messages] of Object.entries(errors)) {
    const firstMessage = messages.at(0);
    if (!firstMessage) {
      continue;
    }

    if (
      field === 'countryId' ||
      field === 'description' ||
      field === 'coordinates' ||
      field === 'forestTypeId' ||
      field === 'productionTimeRange'
    ) {
      setError(field, { message: firstMessage });
    }
  }
}

export function useProductionPlaceForm({
  commodityOriginId,
  materialId,
  onOpenChange,
  open,
  productionDeclarationId,
  productionPlace,
  token,
}: UseProductionPlaceFormParams) {
  const { t } = useTranslation('productionDeclaration');
  const { t: tCommon } = useTranslation('common');
  const {
    mutateAsync: createProductionPlace,
    isPending: isCreatingProductionPlace,
  } = useCreateProductionPlace();
  const {
    mutateAsync: updateProductionPlace,
    isPending: isUpdatingProductionPlace,
  } = useUpdateProductionPlace();

  const form = useForm<ProductionPlaceFormValues>({
    defaultValues: getDefaultValues(productionPlace),
    mode: 'onChange',
    resolver: zodResolver(productionPlaceSchema),
  });

  useEffect(() => {
    form.reset(open ? getDefaultValues(productionPlace) : defaultValues);
    form.clearErrors();
  }, [form, open, productionPlace]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const variables = {
        countryId: Number(values.countryId),
        coordinates: values.coordinates,
        commodityOriginId,
        description: values.description.trim(),
        forestTypeId: Number(values.forestTypeId),
        materialId,
        productionDeclarationId,
        productionTimeRange: values.productionTimeRange.trim(),
        token,
      };

      if (productionPlace) {
        await updateProductionPlace({
          ...variables,
          productionPlaceId: productionPlace.id,
        });
      } else {
        await createProductionPlace(variables);
      }

      toast.success(
        t(
          productionPlace
            ? 'materialCommodityOrigins.cards.productionPlace.updateSuccess'
            : 'materialCommodityOrigins.cards.productionPlace.createSuccess',
        ),
      );
      onOpenChange(false);
    } catch (error) {
      setServerValidationErrors(error, form.setError);

      toast.error(tCommon('error'), {
        description: t(
          productionPlace
            ? 'materialCommodityOrigins.cards.productionPlace.updateError'
            : 'materialCommodityOrigins.cards.productionPlace.createError',
        ),
      });
    }
  });

  return {
    form,
    isSubmitting:
      isCreatingProductionPlace ||
      isUpdatingProductionPlace ||
      form.formState.isSubmitting,
    onSubmit,
  };
}
